import { lazy, ComponentType } from 'react';

const flagKey = (key: string) => `chaptery-chunk-reload:${key}`;

/**
 * React.lazy for a route whose chunk can vanish mid-session: a deploy deletes the old
 * chunks, and a tab opened before it then asks for a file that no longer exists (#848).
 * React caches that rejection, so the page would stay broken. One reload fetches the new
 * build. A flag in sessionStorage keeps a second failure from looping: that one rethrows
 * to the route error page. A success clears the flag. With storage blocked we cannot
 * tell whether we already reloaded, so we rethrow rather than risk a loop.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  key: string,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      try { sessionStorage.removeItem(flagKey(key)); } catch { /* storage unavailable */ }
      return mod;
    } catch (err) {
      let canReload = false;
      try {
        if (!sessionStorage.getItem(flagKey(key))) {
          sessionStorage.setItem(flagKey(key), '1');
          canReload = true;
        }
      } catch { /* storage unavailable: do not risk a reload loop */ }
      if (!canReload) throw err;
      window.location.reload();
      // Hold the Suspense fallback while the page reloads.
      return new Promise<never>(() => {});
    }
  });
}
